import { Component, OnInit } from '@angular/core';
import { ApiService } from '../../../../shared/services/api.service';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  standalone: false,
  selector: 'app-country-remittance-config',
  templateUrl: './remittance-config.component.html',
  styleUrls: ['./remittance-config.component.scss']
})
export class CountryRemittanceConfigComponent implements OnInit {
  countryId: string = '';
  countryDetails: any = {};
  remittanceConfig: any = {
    allowRemittanceFromAll: false,
    allowedRemittance: []
  };
  allCountries: any[] = [];
  availableCountries: any[] = [];
  allowedCountries: any[] = [];
  selectedCountryId: string = '';
  selectedAllowedCountryId: string = '';
  loading: boolean = false;

  constructor(
    private apiService: ApiService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.countryId = params['id'];
      this.loadData();
    });
  }

  loadData(): void {
    this.loading = true;
    this.loadCountryDetails()
      .then(() => this.loadAllCountries())
      .then(() => this.loadRemittanceConfig())
      .finally(() => {
        this.loading = false;
      });
  }

  loadCountryDetails(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.apiService.getData(`/admin/country/get?id=${this.countryId}`).subscribe(
        (response: any) => {
          if (response && response.data) {
            this.countryDetails = response.data;
          }
          resolve();
        },
        (error) => {
          console.error('Error loading country details:', error);
          reject(error);
        }
      );
    });
  }

  loadAllCountries(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.apiService.getData('/admin/get-data?type=country&_limit=500').subscribe(
        (response: any) => {
          if (response && response.data) {
            this.allCountries = response.data;
            this.updateAvailableCountries();
          }
          resolve();
        },
        (error) => {
          console.error('Error loading all countries:', error);
          reject(error);
        }
      );
    });
  }

  loadRemittanceConfig(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.apiService.getData(`/admin/remittance-allowed-countries/${this.countryId}`).subscribe(
        (response: any) => {
          if (response && response.data) {
            this.remittanceConfig = {
              allowRemittanceFromAll: response.data.allowRemittanceFromAll || false,
              allowedRemittance: response.data.allowedRemittance || []
            };
            this.updateAllowedCountries();
            this.updateAvailableCountries();
          }
          resolve();
        },
        (error) => {
          console.error('Error loading remittance config:', error);
          reject(error);
        }
      );
    });
  }

  updateAllowedCountries(): void {
    this.allowedCountries = this.allCountries.filter(country => 
      this.remittanceConfig.allowedRemittance.includes(country._id)
    ).sort((a, b) => {
      const nameA = a.displayName || '';
      const nameB = b.displayName || '';
      return nameA.localeCompare(nameB);
    });
  }

  updateAvailableCountries(): void {
    this.availableCountries = this.allCountries.filter(country => 
      !this.remittanceConfig.allowedRemittance.includes(country._id)
    ).sort((a, b) => {
      const nameA = a.displayName || '';
      const nameB = b.displayName || '';
      return nameA.localeCompare(nameB);
    });
  }

  onAllowAllChange(): void {
    if (this.remittanceConfig.allowRemittanceFromAll) {
      this.remittanceConfig.allowedRemittance = [];
      this.allowedCountries = [];
    }
    this.updateAvailableCountries();
  }

  isCountrySelected(countryId: string): boolean {
    return this.remittanceConfig.allowedRemittance.includes(countryId);
  }

  addCountry(): void {
    if (!this.selectedCountryId) return;

    const selectedCountry = this.allCountries.find(country => country._id === this.selectedCountryId);
    if (selectedCountry && !this.isCountrySelected(this.selectedCountryId)) {
      this.remittanceConfig.allowedRemittance.push(this.selectedCountryId);
      this.updateAllowedCountries();
      this.updateAvailableCountries();
      this.selectedCountryId = '';
    }
  }

  removeCountry(): void {
    if (!this.selectedAllowedCountryId) return;

    const index = this.remittanceConfig.allowedRemittance.indexOf(this.selectedAllowedCountryId);
    if (index > -1) {
      this.remittanceConfig.allowedRemittance.splice(index, 1);
      this.updateAllowedCountries();
      this.updateAvailableCountries();
      this.selectedAllowedCountryId = '';
    }
  }

  successMessage: string = '';
  errorMessage: string = '';

  saveRemittanceConfig(): void {
    this.successMessage = '';
    this.errorMessage = '';
    if (!this.remittanceConfig.allowRemittanceFromAll && (!this.remittanceConfig.allowedRemittance || this.remittanceConfig.allowedRemittance.length === 0)) {
      this.errorMessage = 'Please select at least one allowed country or enable "Allow remittance from all countries"';
      return;
    }
    this.loading = true;
    const requestData = {
      countryId: this.countryId,
      allowRemittanceFromAll: this.remittanceConfig.allowRemittanceFromAll,
      allowedRemittance: this.remittanceConfig.allowedRemittance
    };

    this.apiService.postData('/admin/remittance-allowed-countries', requestData).subscribe(
      (response: any) => {
        if (response && !response.error) {
          this.successMessage = 'Remittance Configuration updated successfully';
        } else {
          this.errorMessage = response.message || 'Failed to save Remittance Configuration';
        }
        this.loading = false;
      },
      (error) => {
        this.errorMessage = (error.error && error.error.message) || error.message || 'Failed to save Remittance Configuration';
        this.loading = false;
      }
    );
  }

  resetForm(): void {
    this.loadRemittanceConfig();
  }

  goBack(): void {
    this.router.navigate(['/app/country']);
  }
}
